import { useTranslate } from '@ur-solutions/react-hooks'

import { parseActionFields } from '@/modules/timesheets/absence-details/absence-logs/util'

type TimeEntryActionLog = {
  action: string
  actionFields?: unknown
  createdAt: string
  user?: {
    fullName?: string | null
  } | null
}

type TimeEntryWithLatestActionLog = {
  latestActionLog?: TimeEntryActionLog | null
}

type FormatedLatestAction = (
  log: TimeEntryActionLog | null | undefined
) => string

/** Returns the entry with the most recent action log, along with that log. */
export const getLatestTimeEntryByActionLog = <
  TEntry extends TimeEntryWithLatestActionLog,
>(
  timeEntries: readonly TEntry[]
) => {
  return timeEntries.reduce<{
    entry: TEntry
    actionLog: TimeEntryActionLog
  } | null>((latest, timeEntry) => {
    const current = timeEntry.latestActionLog
    if (!current) return latest
    if (!latest) return { entry: timeEntry, actionLog: current }

    return new Date(current.createdAt).getTime() >
      new Date(latest.actionLog.createdAt).getTime()
      ? { entry: timeEntry, actionLog: current }
      : latest
  }, null)
}

export const useFormatTimeEntryLastAction = (): FormatedLatestAction => {
  const translations = useTranslate({
    created: 'absence.details.log.action-created',
    updated: 'absence.details.log.action-updated',
    deleted: 'absence.details.log.action-deleted',
    unknown: 'common.unknown',
    start: 'common.start',
    end: 'common.end',
    pause: 'common.pause',
    project: 'common.project',
    department: 'common.department',
    salaryCodeOrdinary: 'common.salary-code',
    salaryCodeOvertime: 'common.salary-code-if-overtime',
    overtimeMode: 'common.overtime-mode',
    diet: 'common.diet',
    approved: 'timesheets.absence-approved',
    comment: 'common.comment',
    fromTerminal: 'common.terminal',
    mergeOverride: 'timesheets.time-entry-merge-override',
    mergedWith: 'timesheets.time-entry-merged-with',
    timeEntry: 'timesheets.timeentry',
  })

  const fieldLabelMap: Record<string, string> = {
    datetime_start: translations.start,
    datetime_end: translations.end,
    pause: translations.pause,
    project_id: translations.project,
    facility_id: translations.project,
    department_id: translations.department,
    salary_code_ordinary_id: translations.salaryCodeOrdinary,
    salary_code_overtime_id: translations.salaryCodeOvertime,
    overtime_mode: translations.overtimeMode,
    diet: translations.diet,
    approved: translations.approved,
    comment: translations.comment,
    from_terminal: translations.fromTerminal,
    merge_override: translations.mergeOverride,
    merged_with_time_entry_id: translations.mergedWith,
  }

  const getActionLabel = (action: string) => {
    const normalized = action.toUpperCase()
    if (normalized === 'CREATED') return translations.created
    if (normalized === 'UPDATED') return translations.updated
    if (normalized === 'DELETED') return translations.deleted
    return ''
  }

  const formatLatestAction: FormatedLatestAction = log => {
    if (!log) return ''

    const actionLabel = getActionLabel(log.action)
    const name = log.user?.fullName?.trim() || translations.unknown
    const fields = Object.keys(parseActionFields(log.actionFields ?? {}))

    const target = fields.length
      ? fields
          .map(f => (fieldLabelMap[f] ?? f.replaceAll('_', ' ')).toLowerCase())
          .join(', ')
      : translations.timeEntry.toLowerCase()

    return [name, actionLabel, target].filter(Boolean).join(' ')
  }

  return formatLatestAction
}
